import {
  LanguageCode,
  LanguageName,
  languages,
} from 'app/utils/supportedLanguages';

/**
 * The API payloads library exports all the supported languages, but some of
 * them are still not translated on Crwodin. This list is used to avoid the user
 * to enable an untranslated language in a Messenger. To see the latest
 * translated languages, see
 * https://github.com/dixahq/messenger-widget/blob/develop/src/shared/language/translatedLocales.ts
 */
const translatedLanguages = [
  'ar',
  'da',
  'de',
  'en',
  'es',
  'et',
  'fi',
  'fr',
  'hu',
  'is',
  'it',
  'ja',
  'lt',
  'lv',
  'nl',
  'no',
  'pl',
  'pt',
  'ru',
  'sv',
  'zh',
] as const satisfies readonly LanguageCode[];

export type MessengerTranslatedLanguage = (typeof translatedLanguages)[number];

const translatedLanguagesMap = translatedLanguages.reduce(
  (acc: Record<string, boolean | undefined>, lang) => {
    acc[lang] = true;
    return acc;
  },
  {},
);

export const isMessengerTranslatedLanguage = (
  code: unknown,
): code is MessengerTranslatedLanguage => {
  return typeof code === 'string' && translatedLanguagesMap[code] === true;
};

/** Messenger supported language that is translated on Crowdin with its name */
export type MessengerLanguage = {
  code: MessengerTranslatedLanguage;
  label: LanguageName;
};

/**
 * A list of supported messenger languages that are translated on Crowdin with
 * preserved sorting order.
 */
export const messengerLanguages = languages.filter(({ code }) =>
  isMessengerTranslatedLanguage(code),
) as readonly MessengerLanguage[];
